import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';
import { Calendar, MapPin, Users, Clock, DollarSign, List, Plane, BookOpen, MoreHorizontal } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useTrips } from '@/hooks/use-trips';
import { useAuth } from '@/contexts/AuthContext';

interface Trip {
  id: string;
  date: Date;
  location: string;
  capacity: number;
  registered: number;
  status: 'open' | 'full' | 'closed';
  duration: string;
  activities: string[];
  price: number;
  included: string[];
  registeredUsers?: string[];
}

interface UpcomingTripsProps {
  organizationId?: string;
}

export function UpcomingTrips({ organizationId }: UpcomingTripsProps) {
  const { t, dir } = useLanguage();
  const { trips, loading, registerForTrip } = useTrips();
  const { currentUser } = useAuth();
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [pendingTrip, setPendingTrip] = useState<Trip | null>(null);
  const [registering, setRegistering] = useState(false);

  const handleRegister = (trip: Trip) => {
    setPendingTrip(trip);
  };

  const confirmRegister = async () => {
    if (!currentUser || !pendingTrip) return;
    setRegistering(true);
    await registerForTrip(pendingTrip.id, currentUser.uid);
    setRegistering(false);
    setPendingTrip(null);
  };

  return (
    <Card>
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-primary" />
            {t('dashboard.customer.dashboard.upcomingTrips.title')}
          </CardTitle>
          <Button variant="outline" size="sm">
            <BookOpen className="h-4 w-4 mr-2" />
            {t('dashboard.customer.dashboard.upcomingTrips.browseAll')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <p className="text-center text-muted-foreground">Loading trips...</p>
        ) : trips.length === 0 ? (
          <p className="text-center text-muted-foreground">
            {t('dashboard.customer.dashboard.upcomingTrips.noTrips')}
          </p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.upcomingTrips.tripDetails.location')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.upcomingTrips.tripDetails.date')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.upcomingTrips.tripDetails.duration')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-right' : 'text-left'}>
                    {t('dashboard.customer.dashboard.upcomingTrips.tripDetails.status')}
                  </TableHead>
                  <TableHead className={dir === 'rtl' ? 'text-left' : 'text-right'}>
                    {t('dashboard.customer.dashboard.upcomingTrips.tripDetails.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.map((trip) => {
                  const isRegistered = currentUser && trip.registeredUsers?.includes(currentUser.uid);
                  const isFull = trip.registeredUsers?.length >= trip.capacity;
                  return (
                    <TableRow key={trip.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {trip.location}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {format(trip.date, 'PPP')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {trip.duration}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          trip.status === 'open' ? 'bg-green-100 text-green-800' :
                          trip.status === 'full' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {trip.status}
                        </span>
                      </TableCell>
                      <TableCell className={dir === 'rtl' ? 'text-left' : 'text-right'}>
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSelectedTrip(trip)}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                          {trip.status === 'open' && (
                            <Button
                              size="sm"
                              onClick={() => handleRegister(trip)}
                              disabled={isRegistered || isFull}
                            >
                              {isRegistered
                                ? t('dashboard.customer.dashboard.upcomingTrips.registered', 'Registered')
                                : isFull
                                  ? t('dashboard.customer.dashboard.upcomingTrips.full', 'Full')
                                  : t('dashboard.customer.dashboard.upcomingTrips.register')}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Confirmation Dialog for Registration */}
      <Dialog open={!!pendingTrip} onOpenChange={open => { if (!open) setPendingTrip(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard.customer.dashboard.upcomingTrips.register')}</DialogTitle>
            <DialogDescription>
              {pendingTrip && (
                <>
                  <div className="mb-2">
                    <span className="font-medium">{t('dashboard.customer.dashboard.upcomingTrips.tripDetails.location')}:</span> {pendingTrip.location}
                  </div>
                  <div className="mb-2">
                    <span className="font-medium">{t('dashboard.customer.dashboard.upcomingTrips.tripDetails.date')}:</span> {format(pendingTrip.date, 'PPP')}
                  </div>
                  <div className="mb-2">
                    <span className="font-medium">{t('dashboard.customer.dashboard.upcomingTrips.tripDetails.duration')}:</span> {pendingTrip.duration}
                  </div>
                  <div className="mb-2">
                    <span className="font-medium">{t('dashboard.customer.dashboard.upcomingTrips.tripDetails.price')}:</span> ${pendingTrip.price}
                  </div>
                  <div className="mb-2 text-warning">
                    {t('dashboard.customer.dashboard.upcomingTrips.confirmation', 'Are you sure you want to register for this trip?')}
                  </div>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setPendingTrip(null)} disabled={registering}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={confirmRegister} loading={registering}>
              {t('dashboard.customer.dashboard.upcomingTrips.register', 'Register')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedTrip} onOpenChange={() => setSelectedTrip(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedTrip?.location}
            </DialogTitle>
            <DialogDescription>
              {format(selectedTrip?.date || new Date(), 'PPP')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{selectedTrip?.duration}</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <span>${selectedTrip?.price}</span>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">{t('dashboard.customer.dashboard.upcomingTrips.tripDetails.activities')}</h4>
              <div className="flex flex-wrap gap-2">
                {selectedTrip?.activities.map((activity, index) => (
                  <Badge key={index} variant="secondary">
                    {activity}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">{t('dashboard.customer.dashboard.upcomingTrips.tripDetails.included')}</h4>
              <ul className="list-disc list-inside space-y-1">
                {selectedTrip?.included.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>
                {selectedTrip?.registered}/{selectedTrip?.capacity} {t('dashboard.customer.dashboard.upcomingTrips.tripDetails.registered')}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
} 